/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface KYCContextType {
  showKYCModal: (status?: string, message?: string) => void;
  hideKYCModal: () => void;
  isModalOpen: boolean;
}

const KYCContext = createContext<KYCContextType | undefined>(undefined);

interface KYCProviderProps {
  children: ReactNode;
  disabled?: boolean;
}

// Lazy import the modal to avoid circular dependencies
const KYCRequiredModal = React.lazy(() => import('../components/kyc/KYCRequiredModal'));

export const KYCProvider: React.FC<KYCProviderProps> = ({ children, disabled = false }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [kycStatus, setKycStatus] = useState<string>('not_submitted');
  const [message, setMessage] = useState<string | undefined>();

  const showKYCModal = useCallback((status?: string, msg?: string) => {
    if (disabled) return;
    setKycStatus(status || 'not_submitted');
    setMessage(msg);
    setIsModalOpen(true);
  }, [disabled]);

  const hideKYCModal = useCallback(() => {
    setIsModalOpen(false);
    setMessage(undefined);
  }, []);

  // Register the global handler when the component mounts
  useEffect(() => {
    setGlobalKYCModalHandler(disabled ? () => {} : showKYCModal);
    return () => setGlobalKYCModalHandler(() => {});
  }, [showKYCModal, disabled]);

  return (
    <KYCContext.Provider value={{ showKYCModal, hideKYCModal, isModalOpen }}>
      {children}
      {!disabled && (
        <React.Suspense fallback={null}>
          <KYCRequiredModal
            isOpen={isModalOpen}
            onClose={hideKYCModal}
            kycStatus={kycStatus}
            message={message}
          />
        </React.Suspense>
      )}
    </KYCContext.Provider>
  );
};

export const useKYC = (): KYCContextType => {
  const context = useContext(KYCContext);
  if (context === undefined) {
    throw new Error('useKYC must be used within a KYCProvider');
  }
  return context;
};

// Global reference for use in non-React contexts (like axios interceptors)
let globalShowKYCModal: ((status?: string, message?: string) => void) | null = null;

export const setGlobalKYCModalHandler = (handler: (status?: string, message?: string) => void) => {
  globalShowKYCModal = handler;
};

export const getGlobalKYCModalHandler = () => globalShowKYCModal;
